import { SEO } from "@/components/SEO";
import { BookingWidget } from "@/components/BookingWidget";
import { Layout } from "./shared/Layout";
import { PromoRibbon } from "@/components/layout/PromoRibbon";
import { HeroBanner } from "@/components/layout/HeroBanner";
import { FloatingOrbs } from "@/components/layout/FloatingOrbs";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Wrench,
  Disc3,
  Thermometer,
  Zap,
  ShieldCheck,
  Search as SearchIcon,
  ArrowRight,
  Star,
  Clock,
  Users,
  ChevronRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: Wrench,
    title: "Engine & Drivetrain",
    desc: "Oil changes, tune-ups, timing belts, transmission service, and engine diagnostics.",
  },
  {
    icon: Disc3,
    title: "Brakes & Suspension",
    desc: "Brake pads & rotors, suspension repair, wheel alignment, steering, and shocks.",
  },
  {
    icon: SearchIcon,
    title: "Tires",
    desc: "Sales, installation, seasonal swaps, storage, balancing, and rotation.",
  },
  {
    icon: Thermometer,
    title: "Cooling & Climate",
    desc: "A/C recharge & repair, coolant flush, heater core, and radiator service.",
  },
  {
    icon: Zap,
    title: "Electrical & Diagnostics",
    desc: "Battery, alternator, starter, check engine light, and full OBD diagnostics.",
  },
  {
    icon: ShieldCheck,
    title: "Inspections",
    desc: "Safety (MTO), pre-purchase inspections, and seasonal vehicle checkups.",
  },
];

const BRANDS = [
  "Michelin", "Bridgestone", "Goodyear", "Continental",
  "Pirelli", "Hankook", "Toyo", "Yokohama",
];

export default function Home() {
  return (
    <Layout>
      <SEO
        title="Full-Service Auto Repair & Tires in Milton, ON"
        description="Full-service auto repair and tire shop in Milton, Ontario. Oil changes, brakes, engine diagnostics, tire sales & installation. 5-star rated, all makes & models."
        canonical="/"
      />
      <PromoRibbon />

      {/* ── Hero — asymmetric with tire search on right ── */}
      <HeroBanner
        eyebrow="APA Automotive — Milton, Ontario"
        title="Your Full-Service Garage in Milton"
        titleAccent="Full-Service"
        subtitle="From oil changes to tire installation — all makes, all models, done right."
        primaryHref="/book"
        primaryLabel="Book a Service"
        secondaryHref="/shop"
        secondaryLabel="Shop Tires"
        rightContent={
          <div className="w-full max-w-md space-y-4">
            <div className="px-2">
              <img
                src="/images/logo-lockup.png"
                alt="APA Automotive logo"
                className="mx-auto h-auto w-full max-w-[18rem] drop-shadow-[0_24px_70px_rgba(0,0,0,0.45)]"
                width="1536"
                height="1024"
                decoding="async"
              />
              <p className="mt-3 text-center text-xs font-semibold uppercase tracking-[0.22em] text-white/55">
                Tires, service, and diagnostics
              </p>
            </div>

            <form
              className="w-full glass-card rounded-2xl p-6"
              onSubmit={(e) => {
                e.preventDefault();
                const val = (document.getElementById("qf-size") as HTMLInputElement)?.value || "";
                window.location.href = `/shop?q=${encodeURIComponent(val.trim())}`;
              }}
            >
              <p className="mb-4 text-sm font-semibold text-white/80">Find your tire size</p>
              <div className="flex gap-3">
                <input
                  id="qf-size"
                  aria-label="Tire size"
                  placeholder="225/65R17"
                  inputMode="text"
                  className="flex-1 rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-lg font-mono tracking-widest text-white placeholder:text-white/30 focus:border-[#00AADD]/50 focus:outline-none focus:ring-2 focus:ring-[#00AADD]/20"
                />
                <Button type="submit" variant="automotive" size="lg" className="rounded-xl px-6 shadow-[0_0_24px_rgba(0,170,221,0.4)]">
                  <SearchIcon className="size-4" aria-hidden />
                </Button>
              </div>
              <p className="mt-3 text-[11px] text-white/35">
                Check your sidewall or door placard. Need help?{" "}
                <a className="text-[#00AADD]/70 hover:text-[#00AADD]" href="tel:2898516911">(289) 851-6911</a>
              </p>
            </form>
          </div>
        }
      />

      {/* ── Stats Row (light) ── */}
      <section className="section-alt py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Wrench, value: "15+", label: "Services Offered" },
              { icon: Users, value: "All", label: "Makes & Models" },
              { icon: Clock, value: "Same-Day", label: "Available" },
              { icon: Star, value: "5.0", label: "Google Rating" },
            ].map(({ icon: Icon, value, label }) => (
              <div key={label} className="flex flex-col items-center gap-1.5">
                <Icon className="size-5 text-primary/60" aria-hidden />
                <span className="stat-number text-3xl sm:text-4xl">{value}</span>
                <p className="text-sm text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services Preview ── */}
      <section className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">What We Do</p>
            <h2 className="mt-2 text-4xl font-bold sm:text-5xl">
              <span className="gradient-text">Complete Automotive Care</span>
            </h2>
            <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything your vehicle needs under one roof — no need to drive across town.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map(({ icon: Icon, title, desc }) => (
              <Link
                key={title}
                to="/services"
                className="card-premium p-6 group cursor-pointer"
              >
                <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/15 transition-colors">
                  <Icon className="size-5 text-primary" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-foreground">{title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{desc}</p>
                <span className="mt-3 inline-flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more <ArrowRight className="size-3.5 ml-1" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button asChild variant="automotive" size="lg" className="rounded-full px-10 shadow-[0_0_24px_rgba(0,170,221,0.35)]">
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Tire Shop Callout (asymmetric) ── */}
      <section className="section-alt py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Live Inventory</p>
              <h2 className="mt-2 text-4xl font-bold text-foreground sm:text-5xl">
                Shop Tires <span className="gradient-text">Online</span>
              </h2>
              <p className="mt-3 text-lg text-muted-foreground leading-relaxed max-w-lg">
                Browse 500+ tire models from our network of distribution warehouses. Search by size, compare prices, and order directly — most are ready within 24 hours.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Button asChild variant="automotive" size="lg" className="rounded-full px-8 shadow-[0_0_20px_rgba(0,170,221,0.35)]">
                  <Link to="/shop">Browse Inventory</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full px-6">
                  <Link to="/tire-size-guide">Size Guide</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <FloatingOrbs variant="blue" />
              <div className="card-premium p-6 relative">
                <div className="space-y-3">
                  {["All-Season", "Winter", "Performance", "Truck/SUV"].map((season) => (
                    <div key={season} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="size-2 rounded-full bg-primary" />
                        <span className="text-sm font-medium text-foreground">{season}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">100+ options</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Starting from</span>
                  <span className="text-2xl font-bold text-foreground">$89</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust / Brands ── */}
      <section className="py-10 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Trusted Brands</p>
          <h2 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">
            We Carry the Best
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-x-10 gap-y-4">
            {BRANDS.map((brand) => (
              <span key={brand} className="text-lg font-semibold text-muted-foreground/60 hover:text-foreground transition-colors">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section-alt py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-foreground text-center sm:text-4xl">Common Questions</h2>
          <div className="mt-6 space-y-3">
            {[
              {
                q: "What services do you offer?",
                a: "We're a full-service garage — oil changes, brakes, suspension, diagnostics, A/C, electrical, engine work, tires, and inspections. The only things we don't do are muffler/exhaust and bodywork.",
              },
              {
                q: "Do I need an appointment?",
                a: "Appointments are recommended but not always required. Book online or call us — same-day availability is often possible.",
              },
              {
                q: "How do I order tires online?",
                a: "Use the tire size on your sidewall or door placard, search our live inventory, add to cart, and check out. Most orders are ready within 24 hours.",
              },
              {
                q: "What are your hours?",
                a: "Monday through Friday 8 AM – 5 PM, Saturday 10 AM – 4 PM. Closed Sundays and statutory holidays.",
              },
            ].map(({ q, a }) => (
              <details key={q} className="group card-premium p-4 cursor-pointer">
                <summary className="flex items-center justify-between text-sm font-semibold text-foreground list-none">
                  {q}
                  <ChevronRight className="size-4 text-primary transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed pr-8">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Book a Service ── */}
      <section className="py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Appointments</p>
            <h2 className="mt-2 text-4xl font-bold sm:text-5xl">
              <span className="gradient-text">Book a Service</span>
            </h2>
            <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
              Pick a service, choose a time, and we'll take care of the rest.
            </p>
          </div>
          <div className="card-premium p-5 sm:p-6">
            <BookingWidget />
          </div>
        </div>
      </section>

      {/* ── Final CTA (dark) ── */}
      <section className="dark-surface py-14 text-center">
        <div className="relative max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white sm:text-5xl">Ready to get started?</h2>
          <p className="text-white/45 mt-3 text-lg">Book a service online or give us a call — we'll take care of the rest.</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild variant="automotive" size="lg" className="rounded-full px-10 shadow-[0_0_30px_rgba(0,170,221,0.5)]">
              <Link to="/book">Book a Service</Link>
            </Button>
            <a
              href="tel:2898516911"
              className="inline-flex h-11 items-center justify-center rounded-full border border-white/20 bg-white/[0.06] px-8 text-sm font-medium text-white/80 backdrop-blur-sm transition-all hover:bg-white/[0.12] hover:border-white/30"
            >
              Call (289) 851-6911
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
